import React from 'react';
import { color } from '@/styles';
import { IMdResident } from '@/types/api/medical/getResidents';
import { css } from '@emotion/react';
import DfreeGraphListItemHeader from './dfreeGraphListItemHeader';
import UrineValueView from './urineValueView';
import UrineVolumeView from './urineVolumeView';
import { isValueView } from '@/utils/dfreeGraph';
import { DfreeGraphViewType } from '@/constants';
import { DfreeGraph } from './dfreeGraph';
import NotificationCover from '../notificationCover';
import { useTranslation } from 'react-i18next';
import { NotificationCoverConfig } from '@/types/notificationCover';

interface Props {
  resident: IMdResident;
  dateRange: { start: string; end: string };
  viewType: DfreeGraphViewType;
  openRecordModal: () => void;
  resetNotification: (coverType?: string) => void;
}

const ResidentItemContainer = css`
  position: relative;
  background: ${color.white};
  box-sizing: border-box;
  border: var(--border-card);
  border-radius: 8px;
  margin-bottom: 16px;
  .highcharts-scatter-series {
    .highcharts-point {
      opacity: 1;
    }
  }
`;

const ResidentItemContent = css`
  padding: 16px 18px 10px 24px;
  display: flex;
  border-top: 1px solid ${color.border_gray};
`;

const GraphContainer = css`
  flex: 1;
  min-width: 0;
`;

const Icon = css`
  flex: 0 0 40px;
  width: 40px;
  height: auto;
`;

const DfreeGraphListItem = ({ resident, dateRange, viewType, openRecordModal, resetNotification }: Props) => {
  const { t } = useTranslation();

  const notificationCoverConfig: NotificationCoverConfig = {
    bigBladderNotification: {
      colorCode: color.notification_yellow,
      icon: <img src='/images/notification/large_icon/large_bigbladder.svg' alt='そろそろ通知' css={Icon} />,
      message: t('exceededNotificationThreshold', 'そろそろラインを超えました'),
      buttonText: 'OK',
      showUrineVolume: resident.coverUrineVolume !== null,
      buttonAction: () => resetNotification('bigBladder'),
    },
    urinatedNotification: {
      colorCode: color.notification_pink,
      icon: <img src='/images/notification/large_icon/large_urinated.svg' alt='出たかも通知' css={Icon} />,
      message: t('checkMayHaveUrinated', 'でたかもしれません'),
      buttonText: 'OK',
      showUrineVolume: resident.coverUrineVolume !== null,
      buttonAction: () => resetNotification('urination'),
    },
    gotUpNotification: {
      colorCode: color.notification_red,
      icon: <img src='/images/notification/large_icon/large_gotUp.svg' alt='起き上がり通知' css={Icon} />,
      message: t('detectedGettingUp', '起き上がりを検知しました'),
      buttonText: 'OK',
      showUrineVolume: resident.coverUrineVolume !== null,
      buttonAction: () => resetNotification('gotUp'),
    },
    urineResetNotification: {
      colorCode: color.notification_navy,
      icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
      message: t('MeasurementHasRestarted', '測定が再開されました'),
      buttonText: 'OK',
      showUrineVolume: false,
      buttonAction: () => resetNotification('urineReset'),
    },
    sensorErrorNotification: {
      colorCode: color.notification_navy,
      icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
      message: t('DFreeMayNotBeAttached', 'センサが外れています'),
      buttonText: 'OK',
      showUrineVolume: false,
      buttonAction: () => resetNotification('sensorError'),
    },
    upsideDownNotification: {
      colorCode: color.notification_navy,
      icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
      message: t('DFreeMayBeUpsideDown', 'センサの向きが上下逆です'),
      buttonText: 'OK',
      showUrineVolume: false,
      buttonAction: () => resetNotification('upsideDown'),
    },
  };

  return (
    <div css={ResidentItemContainer} key={resident.id}>
      <DfreeGraphListItemHeader resident={resident} />
      <div css={ResidentItemContent}>
        {isValueView(viewType) ? <UrineValueView resident={resident} /> : <UrineVolumeView resident={resident} />}
        <div css={GraphContainer}>
          <DfreeGraph resident={resident} dateRange={dateRange} viewType={viewType} />
        </div>
      </div>
      <NotificationCover resident={resident} config={notificationCoverConfig} />
    </div>
  );
};
export default DfreeGraphListItem;
